import type { TFunction } from "i18next";

/**
 * Names the API builds from fixed English templates and stores — run names,
 * scope labels, payslip titles, settlement lines (B53). They are recognised
 * here and rebuilt in the user's language; anything else (a name someone
 * typed, a leave type, a branch) is shown as it is.
 */

type FormatDate = (date: Date, pattern: string) => string;

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/** "June" / "Jun" → 5; anything else → -1. */
const monthIndex = (word: string) => {
  const w = word.toLowerCase();
  return MONTHS.findIndex((m) => m === w || m.slice(0, 3) === w);
};

const MONTH_YEAR = /^([A-Za-z]+) (\d{4})$/;

/** "June 2026" → "يونيو 2026"; anything else unchanged. */
export const localizeMonthLabel = (
  label: string | null | undefined,
  formatDate: FormatDate,
): string => {
  if (!label) return "";
  const m = label.trim().match(MONTH_YEAR);
  if (!m || monthIndex(m[1]) < 0) return label;
  return formatDate(new Date(Number(m[2]), monthIndex(m[1]), 1), "LLLL yyyy");
};

const isoDay = (iso: string) => {
  const [y, mo, d] = iso.split("-").map(Number);
  return new Date(y, mo - 1, d);
};

export const displayRunName = (
  t: TFunction,
  name: string | null | undefined,
  formatDate: FormatDate,
): string => {
  if (!name) return "";
  const regular = name.match(/^([A-Za-z]+) (\d{4}) Payroll$/);
  if (regular && monthIndex(regular[1]) >= 0) {
    return t("company:payroll_names.run_month", {
      period: localizeMonthLabel(`${regular[1]} ${regular[2]}`, formatDate),
    });
  }
  const settlement = name.match(
    /^Final Settlement - (.+) - (\d{4}-\d{2}-\d{2})$/,
  );
  if (settlement) {
    return t("company:payroll_names.run_settlement", {
      name: settlement[1],
      date: formatDate(isoDay(settlement[2]), "d MMM yyyy"),
    });
  }
  return name;
};

export const displayScopeLabel = (
  t: TFunction,
  label: string | null | undefined,
): string => {
  if (!label) return "";
  if (label === "Selected Employees")
    return t("company:payroll_names.scope_selected_employees");
  if (label === "Branch") return t("company:payroll_names.scope_branch");
  const m = label.match(/^(Department|Group|Custom): (.+)$/);
  if (m) {
    const key = {
      Department: "scope_department",
      Group: "scope_group",
      Custom: "scope_custom",
    }[m[1] as "Department" | "Group" | "Custom"];
    return t(`company:payroll_names.${key}`, { names: m[2] });
  }
  return label;
};

export const displayPayslipTitle = (
  t: TFunction,
  title: string | null | undefined,
  formatDate: FormatDate,
): string => {
  if (!title) return "";
  if (title === "Payslip") return t("company:payroll_names.payslip");
  const m = title.match(/^(.+) Payslip$/);
  if (m && MONTH_YEAR.test(m[1])) {
    return t("company:payroll_names.payslip_period", {
      period: localizeMonthLabel(m[1], formatDate),
    });
  }
  return title;
};

/** Lines a final settlement adds by itself. */
export const displaySettlementLineName = (
  t: TFunction,
  name: string | null | undefined,
): string => {
  if (!name) return "";
  if (name === "End of Service Gratuity")
    return t("company:payroll_names.line_gratuity");
  const reimbursement = name.match(/^Expense Reimbursement \((.+)\)$/);
  if (reimbursement)
    return t("company:payroll_names.line_reimbursement", {
      reference: reimbursement[1],
    });
  const advance = name.match(/^Leave Taken in Advance \((.+)\)$/);
  if (advance)
    return t("company:payroll_names.line_leave_advance", {
      leave: advance[1],
    });
  const encashment = name.match(/^(.+) Encashment$/);
  if (encashment)
    return t("company:payroll_names.line_encashment", {
      leave: encashment[1],
    });
  return name;
};
